export const featuredArticles = [
  {
    id: "appointment-note",
    slug: "prepare-for-a-psychiatry-appointment",
    title: "Prepare for a psychiatry appointment with one page",
    excerpt: "A short note can hold the details that become hard to recall once a consultation begins.",
    category: "Psychiatry notes",
    readingTime: "4 min",
    author: "Dr. Neerja Gidwani",
    publishedAt: "2026-07-18T08:30:00.000Z",
    coverImage: "/images/notes/appointment-note.jpg",
    coverAlt: "An open blank notebook, pencil, paper tabs, and a small clock on a warm desk.",
    visualTone: "sage",
    body: `A consultation asks you to describe experiences that may feel tangled, private, or hard to date. You may remember one detail on the journey home and forget the question you meant to ask. A one-page note gives the conversation a starting point.

I would keep that page practical. Write for yourself first. You can decide what to share once you are in the room.

## Start with the change you noticed

Name the concern in your own words. Add when you first noticed it, how often it occurs, and whether its pattern has changed. Dates help, but an estimate such as “around the start of this semester” still gives useful context.

Include the parts of daily life that feel different. Sleep, appetite, energy, concentration, work, study, relationships, and routines can help a clinician understand the shape of the difficulty. Mention a major stressor or life change if it belongs in the story.

## Bring the health details

List medicines, vitamins, herbal products, and supplements. Add any allergies or past side effects you remember. A photo of the labels can help when the names or doses are hard to recall.

Family history may matter too. You do not need a complete family record. Write down what you know and mark the gaps.

## Put your questions on the page

Questions often disappear once a new conversation begins. Write two or three before the visit. You might ask:

- What possibilities are you considering?
- What should I track before the next visit?
- What are the options, benefits, and risks I should understand?
- When should I seek help sooner?

The clinician may need to ask questions you did not expect. You can ask why a question matters. You can also say that you need a moment before answering.

## Decide whether you want company

A trusted person can sit beside you and help capture details from the visit. Some people want that person present for the whole visit. Others prefer to begin alone. Choose the arrangement that gives you more room to speak.

Your note does not need to prove a diagnosis. It gives you a way to describe what you have noticed and ask for help with the next step.

## Sources

[NIMH: Tips for talking with a health care provider about your mental health](https://www.nimh.nih.gov/health/publications/tips-for-talking-with-your-health-care-provider)

This article offers education, not a diagnosis or personal medical advice.`,
  },
  {
    id: "support-listening",
    slug: "support-someone-start-with-listening",
    title: "Someone you care about is struggling. Start with listening",
    excerpt: "Care can begin with one observation, one open question, and enough quiet for an honest answer.",
    category: "For families",
    readingTime: "4 min",
    author: "Dr. Neerja Gidwani",
    publishedAt: "2026-07-18T08:20:00.000Z",
    coverImage: "/images/notes/support-listening.jpg",
    coverAlt: "Two handmade ceramic cups angled toward each other on a wooden table.",
    visualTone: "plum",
    body: `You may notice that someone has withdrawn, stopped replying, or lost interest in routines they once kept. Concern can make you search for the perfect sentence. A plain check-in gives the other person more room.

Try naming what you noticed without assigning a cause: “You have seemed quieter this week, and I wanted to check in. How have things been for you?”

## Let them choose the pace

The person may talk, change the subject, or say they are fine. Do not force a disclosure. You can leave the door open: “You do not have to talk now. I am here if you want company or a conversation later.”

An open question can help when they do want to speak. Ask about their experience rather than searching for an explanation. “How has the week felt?” gives them more space than “Are you depressed?”

## Ask what kind of help they want

Listening and problem-solving serve different needs. Ask which one would help: “Would you like me to listen, help think through options, or sit with you for a while?”

Then follow their answer. Put your phone down. Leave pauses alone. Reflect the detail they shared instead of replacing it with your own story.

Practical support can carry weight. You might offer to cook, take a walk, make a call, or accompany them to an appointment. Make one clear offer that you can keep.

## Suggest professional support with respect

Ongoing distress or changes in daily life may need professional attention. You can say: “Would you be open to speaking with a mental-health professional? I can help you look for support if you want.”

The person still owns the decision unless immediate safety is at risk. Pressure can close a conversation that took courage to begin.

## Treat danger as urgent

Ask a direct safety question if you believe the person may harm themselves or someone else. Stay with them if you can do so without putting yourself at risk. In India, call emergency services on 112 or Tele-MANAS on 14416.

Supporting someone can affect you too. Speak with a trusted person or professional while protecting the other person’s privacy. Care works better when you know the help you can offer and the help you need to seek.

## Source

[NHS Every Mind Matters: Helping others with mental-health problems](https://www.nhs.uk/every-mind-matters/supporting-others/helping-others/)

This article offers education and cannot replace help from a qualified professional.`,
  },
  {
    id: "thought-record",
    slug: "use-a-thought-record",
    title: "Use a thought record when one moment fills the whole frame",
    excerpt: "Seven prompts can help you examine a difficult thought without treating it as a verdict.",
    category: "Everyday wellbeing",
    readingTime: "4 min",
    author: "Dr. Neerja Gidwani",
    publishedAt: "2026-07-18T08:10:00.000Z",
    coverImage: "/images/notes/thought-record.jpg",
    coverAlt: "A clear glass frame resting over paper strips crossed by one graphite line.",
    visualTone: "peach",
    body: `A difficult moment can produce a quick conclusion: “I ruined everything,” “They must be angry,” or “I cannot handle this.” The thought may feel as solid as the event itself.

A thought record slows the sequence down. Cognitive behavioural therapy uses this exercise to examine the links between situations, thoughts, feelings, and actions. You can try it on paper or in a private note.

## Choose one specific situation

Use a recent moment you can describe in a sentence. “My friend read my message and did not reply” gives you more to examine than “My relationships are a mess.”

Begin with what happened. Leave guesses about motives for the thought section.

## Name the feeling and the thought

List the feeling in a word or two, then rate its intensity from 0 to 100. Next, write the thought that appeared in the moment. Keep the original wording, even if it sounds harsh.

The rating does not measure how serious your experience is. It helps you compare how the feeling changes while you work through the prompts.

## Examine the evidence

Write the facts that support the thought. Then write the facts that do not fit it. Treat both columns with the same care.

You may find that you have limited information. “They did not reply for three hours” is a fact. “They have decided to end the friendship” is one explanation.

## Write a neutral alternative

Build a sentence that respects the facts on both sides. You do not need a cheerful replacement. A neutral thought often feels more credible: “I do not know why they have not replied. I can wait until tomorrow and ask.”

Rate the feeling again after you write the alternative. A small shift still gives you information. No shift can tell you that you may need another form of support.

## Keep the exercise within its limits

A thought record cannot settle every concern. It may feel hard to use during intense distress. Stop if the exercise increases discomfort, and speak with a qualified professional if difficult thoughts persist or affect daily life.

If you may harm yourself or someone else, call emergency services on 112 or Tele-MANAS on 14416. An emergency needs a person, not a worksheet.

## Source

[NHS Every Mind Matters: Thought record](https://www.nhs.uk/every-mind-matters/mental-wellbeing-tips/self-help-cbt-techniques/thought-record/)

This article offers education, not therapy or personal medical advice.`,
  },
  {
    id: "sleep-pattern",
    slug: "sleep-note-more-than-bedtime",
    title: "A sleep note can show more than bedtime",
    excerpt: "A short record can capture timing, awakenings, routines, and the effect that sleep has on the next day.",
    category: "Everyday wellbeing",
    readingTime: "4 min",
    author: "Dr. Neerja Gidwani",
    publishedAt: "2026-07-18T08:00:00.000Z",
    coverImage: "/images/notes/sleep-routine.jpg",
    coverAlt: "A folded linen cloth, closed book, warm lamp, and simple clock on a sage bedside table.",
    visualTone: "dusk",
    body: `Sleep does not fit into one number. Bedtime and waking time matter, but they leave out long stretches awake, waking before the intended time, naps, and the way you feel the next day. A short sleep note can help you see the pattern.

Use the note to describe what happened. Leave judgments such as “I am a terrible sleeper” off the page. A record works best when it stays close to what you noticed.

## Record the night in a few lines

Write down when you went to bed and when you got up. Add an estimate of how long it took to fall asleep, how often you woke, and whether you slept during the day.

You do not need to watch the clock through the night. An estimate gives enough detail for a first conversation. Clock-watching can make the night feel busier.

## Add the hours around sleep

Note what happened before bed. Include caffeine, alcohol, exercise, screen use, work, study, or a difficult conversation when they seem relevant. Write down any shift work or caring duties that changed your routine.

The morning adds another part of the picture. Record your energy, mood, and concentration in plain words. “Heavy eyes during the morning lecture” says more than “bad sleep.”

## Change one part of the routine

The NHS suggests keeping similar sleep and waking times and building a calm bedtime routine. Choose one change that fits your life. You might set a steady waking time or move a stimulating task away from the last part of the evening.

Keep the change for several nights before you judge it. If your week includes shifts, exams, a baby, pain, or another constraint, write that down. A useful plan has to fit the life you have.

## Bring the note when sleep keeps changing

Speak with a qualified clinician if sleep problems persist, cause distress, or affect work, study, relationships, or safety. Bring the note with you. It can help you describe the pattern without having to reconstruct several nights from memory.

Seek prompt medical advice if you have a sudden severe change, feel unsafe, or notice symptoms that concern you. A sleep note supports a conversation. It cannot explain the cause on its own.

## Source

[NHS Every Mind Matters: How to fall asleep faster and sleep better](https://www.nhs.uk/every-mind-matters/mental-wellbeing-tips/how-to-fall-asleep-faster-and-sleep-better/)

This article offers education, not a diagnosis or a personal sleep plan.`,
  },
  {
    id: "worry-time",
    slug: "give-worry-a-time-and-page",
    title: "Give worry a time and a page",
    excerpt: "A short scheduled period can help you record a worry, sort what you can act on, and return to the day.",
    category: "Everyday wellbeing",
    readingTime: "4 min",
    author: "Dr. Neerja Gidwani",
    publishedAt: "2026-07-18T07:50:00.000Z",
    coverImage: "/images/notes/worry-time.jpg",
    coverAlt: "A small sand timer beside a ceramic tray filled with folded blank paper slips.",
    visualTone: "blue",
    body: `A worry can ask for attention each time it returns. Writing it down gives you a place to meet it without letting it set the pace for the whole day.

The NHS describes “worry time” as a short scheduled period for recording concerns and looking for possible action. The technique needs practice. It does not require you to suppress a thought or pretend that a problem has gone away.

## Catch the worry in one sentence

Keep a paper slip or private phone note nearby. Write one sentence when a worry appears. Use the words that came to mind, then return to the task in front of you when you can.

You can tell yourself, “I have recorded this. I will look at it during worry time.” The sentence marks a plan rather than a promise that the thought will disappear.

## Set a short boundary

Choose a 10 or 15 minute period that does not sit close to bedtime if late reflection keeps you awake. Use the same place when that helps. Start a timer, open the list, and take one concern at a time.

Read the sentence and ask whether you can do something practical. A solvable concern needs an action. A hypothetical worry may begin with “what if” and depend on events outside your control.

## Give a solvable concern one action

Write the smallest useful step. Send one email, find one phone number, gather one document, or choose a time for a conversation. Put the step on your calendar if you cannot do it now.

Do not ask the page to solve five problems at once. One action gives you something clear to review.

## Close the page when the timer ends

Mark any action you chose, close the note, and shift attention to a concrete part of the present. You might wash a cup, step outside, or return to the next line of work.

Some worries will return. Record them again if you need to. Speak with a qualified professional if worry causes marked distress, disrupts sleep, or affects daily life. If you may harm yourself or someone else, call emergency services on 112 or Tele-MANAS on 14416.

## Source

[NHS Every Mind Matters: Tackling your worries](https://www.nhs.uk/every-mind-matters/mental-wellbeing-tips/self-help-cbt-techniques/tackling-your-worries/)

This article offers education, not therapy or personal medical advice.`,
  },
  {
    id: "assessment-conversation",
    slug: "what-to-expect-mental-health-assessment",
    title: "What to expect from a mental-health assessment",
    excerpt: "The conversation may cover symptoms, daily life, health, relationships, safety, strengths, and the support you want.",
    category: "Psychiatry notes",
    readingTime: "5 min",
    author: "Dr. Neerja Gidwani",
    publishedAt: "2026-07-18T07:40:00.000Z",
    coverImage: "/images/notes/mental-health-assessment.jpg",
    coverAlt: "Two comfortable empty chairs facing a low table with a blank notepad and glass of water.",
    visualTone: "walnut",
    body: `A mental-health assessment gives you and a clinician time to understand what brought you in and what kind of support may fit. The clinician may ask about more than the concern that appears first. Each area helps place the difficulty in the context of your life.

You can prepare without rehearsing a perfect account. A few notes and questions can carry the details that become hard to recall in a new room.

## The conversation may move across several areas

Expect questions about thoughts, feelings, behaviour, physical health, sleep, appetite, work, study, relationships, substance use, and past experiences. The clinician may also ask about strengths, routines, culture, responsibilities, and what has helped before.

Safety questions form part of many assessments. A clinician may ask about self-harm, suicidal thoughts, harm from another person, or concern about someone else. Direct questions help them understand what support you need.

## You can say what feels hard to discuss

Tell the clinician when a question feels unclear, private, or difficult. Ask why the information matters. You can request a pause or say that you need more time before you answer.

Sharing more detail can help the assessment, but trust may take time. Start with what you can describe. A clinician can explain confidentiality and the situations in which safety concerns require action.

## Bring support if it would help

You may want a friend, relative, or advocate with you. Decide what role you want them to play. They might sit beside you, add an observation, or take notes.

You can also ask to speak alone for part of the appointment. Tell your support person what information you want to keep private before you enter the room.

## Ask what comes next

Before the conversation ends, ask what the clinician understood, what possibilities they are considering, and what the next step may involve. Ask when you should expect follow-up and whom to contact if things change.

Write down unfamiliar terms. You can ask for an explanation in plain language and request time to think about options. The assessment begins a process of understanding. It does not require you to make every decision in one sitting.

## Source

[NHS: Mental health assessments](https://www.nhs.uk/mental-health/social-care-and-your-rights/mental-health-assessments/)

This article offers general education. Assessment practices and patient rights vary by country and service.`,
  },
  {
    id: "teenager-conversation",
    slug: "make-room-teenager-talk",
    title: "Make room for a teenager to talk",
    excerpt: "Begin with one observation, ask an open question, and let the young person choose how much to say.",
    category: "For families",
    readingTime: "4 min",
    author: "Dr. Neerja Gidwani",
    publishedAt: "2026-07-18T07:30:00.000Z",
    coverImage: "/images/notes/talk-with-teenager.jpg",
    coverAlt: "Two pairs of canvas shoes beside stools angled toward each other in a warm family room.",
    visualTone: "terracotta",
    body: `Concern can turn a conversation into a stream of questions. A teenager may hear those questions as pressure, even when you mean to help. Begin with what you noticed and give them room to decide where the conversation goes.

Choose a moment with some privacy and no immediate deadline. A walk, drive, or shared task may feel easier than sitting face to face.

## Name the observation without a verdict

Say what you saw: “You have spent more time alone this week,” or “Mornings have looked hard.” Avoid assigning a cause before they speak.

Follow with one open question: “How have things felt for you?” Then wait. A quiet moment does not mean the conversation has failed.

## Keep curiosity ahead of correction

The NHS advises parents not to judge or assume they know what is wrong. Listen for the part of the story that matters to the teenager. Reflect it back in your own words and ask whether you understood.

Resist the urge to fix every detail while they speak. You can ask, “Would you like me to listen, help you think through options, or give you some space?” Their answer tells you what support may fit that moment.

## Be clear about safety and privacy

Tell the young person how you will treat what they share. Do not promise secrecy that you cannot keep. Explain that you will seek help if you believe they or someone else faces danger.

In situations without immediate danger, involve them in decisions about whom to contact and what information to share. Choice can protect trust.

## Leave the door open

They may not want to talk when you ask. You can say, “You do not have to answer now. I care about you, and I am ready to listen when you want.” Keep ordinary contact going through meals, errands, music, or time in the same room.

Seek professional support when changes persist, distress grows, or daily life becomes hard. If the young person may harm themselves or someone else, stay with them when doing so does not place you at risk and call emergency services on 112 or Tele-MANAS on 14416.

## Source

[NHS: Talking to your teenager](https://www.nhs.uk/mental-health/children-and-young-adults/advice-for-parents/talk-to-your-teenager/)

This article offers general education for families and cannot replace an assessment from a qualified professional.`,
  },
  {
    id: "one-problem",
    slug: "start-one-problem-next-action",
    title: "Start with one problem you can act on",
    excerpt: "Define the problem in one sentence, separate facts from assumptions, and choose a small next action.",
    category: "Everyday wellbeing",
    readingTime: "4 min",
    author: "Dr. Neerja Gidwani",
    publishedAt: "2026-07-18T07:20:00.000Z",
    coverImage: "/images/notes/one-problem-next-step.jpg",
    coverAlt: "A blank index card, graphite pencil, and three small wooden blocks arranged as steps.",
    visualTone: "sand",
    body: `Several problems can blend into one heavy sentence: “Everything is going wrong.” A problem-solving page gives each concern its own boundary. Start with the concern that allows some action.

The exercise will not remove uncertainty. It helps you decide where to place effort.

## Sort the concerns

Write each concern on a separate line. Ask whether an action from you could change part of it. “I have not replied to the college email” allows action. “What if everyone thinks I am failing?” depends on assumptions and other people’s thoughts.

Keep the solvable concerns on one side. Put hypothetical worries on another page for reflection or worry time.

## Define one problem with facts

Choose one solvable concern and describe it in a sentence. Include the current situation and the obstacle. Leave blame and predictions out.

“I need to request an extension before Friday, and I have not found the form” gives you a place to begin. You can find the form, ask a classmate, or contact the office.

## Consider two possible actions

Write two actions without judging them while you write. Then look at the time, support, and information each one needs. Choose the action that fits the present situation.

Make the plan concrete. Name what you will do, when you will begin, and whom you may ask for help. Put the first step where you will see it.

## Review the result

After you try the action, record what happened. The result may solve the problem, change it, or show that you need another approach. Each outcome gives you new information.

Some concerns need professional, legal, financial, or practical support. A worksheet cannot provide expertise that the problem requires. Ask for the right kind of help when the next action sits outside your role or capacity.

## Source

[NHS Every Mind Matters: Problem solving](https://www.nhs.uk/every-mind-matters/mental-wellbeing-tips/self-help-cbt-techniques/problem-solving/)

This article offers education, not therapy or advice for a specific personal problem.`,
  },
  {
    id: "medication-questions",
    slug: "questions-medication-conversation",
    title: "Bring better questions to a medication conversation",
    excerpt: "An accurate list and a few written questions can help you understand purpose, use, monitoring, and possible side effects.",
    category: "Psychiatry notes",
    readingTime: "5 min",
    author: "Dr. Neerja Gidwani",
    publishedAt: "2026-07-18T07:10:00.000Z",
    coverImage: "/images/notes/medication-questions.jpg",
    coverAlt: "A blank amber medicine bottle, closed pill organizer, notebook, and pencil on a warm desk.",
    visualTone: "amber",
    body: `A medication discussion can bring unfamiliar names and several decisions into one appointment. An accurate list and written questions can help you take part in that conversation.

Bring the containers, photographs of the labels, or a list from your pharmacy when you can. Include products that did not come from a prescription.

## Build one complete list

Write the name, dose, and time of each prescription medicine. Add over-the-counter medicines, vitamins, herbal products, and supplements. Include allergies and past reactions that you remember.

Tell the clinician if you miss doses, change the timing, or take something in a different way from the label. The information helps them understand what your body has received. It does not invite punishment.

## Ask what the medicine is meant to do

You can ask:

- What concern is this medicine intended to address?
- How and when should I take it?
- What changes should I watch for?
- Which side effects need prompt help?

Ask when the clinician plans to review the medicine and whether you need physical checks or tests. Request written instructions when the plan has several parts.

## Describe effects with timing

If you notice a change, write what happened and when it began. Include the timing in relation to the dose and any effect on sleep, appetite, energy, movement, concentration, or daily tasks.

Contact the prescribing clinician or another qualified provider if you have concerns. Seek urgent medical help for a severe reaction or immediate danger.

## Change medication with clinical guidance

NIMH advises people not to stop a prescribed mental-health medication without help from a health care provider. Some medicines need a planned dose reduction, and abrupt changes can cause harm.

Ask what to do if you miss a dose, want to stop, become pregnant, start another medicine, or use alcohol or other substances. Keep the answer with your medication list.

## Source

[NIMH: Mental Health Medications](https://www.nimh.nih.gov/health/topics/mental-health-medications)

This article offers general education. It does not recommend a medicine, dose, or treatment plan.`,
  },
];
